import React, { useEffect, useState } from "react";
import Select from "react-select";
import PropTypes from "prop-types";
import Swal from "sweetalert2";
import api from "../../../core/api/config";
import { reactSelectStyles } from "../../../shared/styles/reactSelectStyles";
import { maskCpf } from "../../../shared/utils/maskCpf";
import { validateCpfWithMessage } from "../../../shared/utils/validateCpf";

const CreateUserModal = ({ show, onClose, onCreated }) => {
  const [formData, setFormData] = useState({
    nome: "",
    email: "",
    cpf: "",
    estabelecimento: "",
    ibge: null,
    tipo: null,
    tipoDescricao: null,
    ativo: true,
  });

  const [cidadesOptions, setCidadesOptions] = useState([]);
  const [tiposAcessoOptions, setTiposAcessoOptions] = useState([]);
  const [loadingCidades, setLoadingCidades] = useState(false);
  const [loadingTiposAcesso, setLoadingTiposAcesso] = useState(false);
  const [cpfError, setCpfError] = useState("");

  useEffect(() => {
    if (show) {
      listarCidades();
      listarTiposAcesso();
    }
  }, [show]);

  const listarCidades = async () => {
    try {
      setLoadingCidades(true);
      const { data } = await api.get("/api/tracking/cidade/sp");

      setCidadesOptions(
        (data || []).map((c) => ({
          value: c.id,
          label: c.nome,
        })),
      );
    } catch {
      Swal.fire("Erro", "Falha ao carregar cidades", "error");
    } finally {
      setLoadingCidades(false);
    }
  };

  const listarTiposAcesso = async () => {
    try {
      setLoadingTiposAcesso(true);
      const { data } = await api.get("/api/tracking/usuario/tipo-acesso");

      setTiposAcessoOptions(
        (data || []).map((t) => ({
          value: t.id,
          label: t.tipo,
        })),
      );
    } catch {
      Swal.fire("Erro", "Falha ao carregar tipos de acesso", "error");
    } finally {
      setLoadingTiposAcesso(false);
    }
  };

  const handleCriarUsuario = async () => {
    const { valid, message } = validateCpfWithMessage(formData.cpf);

    if (!valid) {
      Swal.fire("Atenção", message, "warning");
      return;
    }

    try {
      const payload = {
        name: formData.nome,
        email: formData.email,
        estabelecimento: formData.estabelecimento,
        ibge: formData.ibge,
        municipio:
          cidadesOptions.find((c) => c.value === formData.ibge)?.label ?? null,
        status: formData.ativo,
        cpf: formData.cpf.replace(/\D/g, ""),
        tipo: formData.tipo,
        tipoDescricao: formData.tipoDescricao,
      };

      await api.post("/api/tracking/usuario/usuario", payload);

      Swal.fire("Sucesso", "Usuário criado com sucesso", "success");
      onCreated();
      onClose();
    } catch {
      Swal.fire("Erro", "Falha ao criar usuário", "error");
    }
  };

  if (!show) return null;

  return (
    <div
      className="modal show d-block"
      style={{ background: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h4>Novo Usuário</h4>
            <button className="btn-close" onClick={onClose} />
          </div>

          <div className="modal-body">
            <div className="row">
              <div className="col-md-6 mb-3">
                <label>Nome</label>
                <input
                  className="form-control"
                  value={formData.nome}
                  onChange={(e) =>
                    setFormData({ ...formData, nome: e.target.value })
                  }
                />
              </div>

              <div className="col-md-6 mb-3">
                <label>Email</label>
                <input
                  className="form-control"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                />
              </div>

              <div className="col-md-6 mb-3">
                <label>CPF</label>

                <input
                  className={`form-control ${cpfError ? "is-invalid" : ""}`}
                  value={formData.cpf}
                  maxLength={14}
                  onChange={(e) => {
                    const value = maskCpf(e.target.value);

                    // atualiza o formulário com CPF mascarado
                    setFormData({
                      ...formData,
                      cpf: value,
                    });

                    // valida e define mensagem de erro
                    const result = validateCpfWithMessage(value);
                    setCpfError(result.valid ? "" : result.message);
                  }}
                />

                {cpfError && <small className="text-danger">{cpfError}</small>}
              </div>

              <div className="col-md-6 mb-3">
                <label>Tipo de Acesso</label>
                <Select
                  styles={reactSelectStyles}
                  options={tiposAcessoOptions}
                  isClearable
                  value={
                    tiposAcessoOptions.find((t) => t.value === formData.tipo) ||
                    null
                  }
                  onChange={(selected) =>
                    setFormData({
                      ...formData,
                      tipo: selected?.value ?? null,
                      tipoDescricao: selected?.label ?? null,
                    })
                  }
                  placeholder={
                    loadingTiposAcesso
                      ? "Carregando tipos..."
                      : "Selecione o tipo"
                  }
                />
              </div>

              <div className="col-md-6 mb-3">
                <label>Município</label>
                <Select
                  styles={reactSelectStyles}
                  options={cidadesOptions}
                  isClearable
                  value={
                    cidadesOptions.find((c) => c.value === formData.ibge) ||
                    null
                  }
                  onChange={(selected) =>
                    setFormData({
                      ...formData,
                      ibge: selected?.value ?? null,
                    })
                  }
                  placeholder={
                    loadingCidades
                      ? "Carregando municípios..."
                      : "Selecione o município"
                  }
                />
              </div>

              <div className="col-md-6 mb-3">
                <label>Status</label>
                <div className="form-check form-switch mt-2">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={formData.ativo}
                    onChange={() =>
                      setFormData({
                        ...formData,
                        ativo: !formData.ativo,
                      })
                    }
                  />
                  <label className="form-check-label ms-2">
                    {formData.ativo ? "Ativo" : "Inativo"}
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button className="btn btn-primary" onClick={handleCriarUsuario}>
              Criar Usuário
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

CreateUserModal.propTypes = {
  show: PropTypes.bool,
  onClose: PropTypes.func,
  onCreated: PropTypes.func,
};

export default CreateUserModal;
